import type { ProtonRole } from './acidBaseCalculations';

/**
 * Reviewed cases for the Acid–Base Calculations tool (study-chemistry-tools-prd.md §8.3).
 * Conditions: dilute aqueous solutions at 25 °C, Kw = 1.0 × 10⁻¹⁴.
 * reviewStatus: 'pending-teaching-review' — see ACID_BASE_REVIEW_STATUS.
 */

/**
 * One species in a Brønsted–Lowry equation. `core` is the formula without its transferable H⁺;
 * `hydrogens` counts only the transferable hydrogens, so a conjugate pair shares one core and
 * differs by exactly one hydrogen and one charge.
 */
export interface BronstedSpecies {
  formula: string;
  core: string;
  hydrogens: number;
  charge: number;
  role: ProtonRole;
}

export interface ConjugateCase {
  /** Stable id. Never rename: saved bindings may reference it. */
  id: string;
  title: string;
  context: string;
  arrow: '→' | '⇌';
  /** Two reactants, then two products, in display order. */
  species: [BronstedSpecies, BronstedSpecies, BronstedSpecies, BronstedSpecies];
  note: string;
}

const water = (role: ProtonRole): BronstedSpecies => ({
  formula: 'H₂O',
  core: 'O',
  hydrogens: 2,
  charge: 0,
  role,
});
const hydronium: BronstedSpecies = {
  formula: 'H₃O⁺',
  core: 'O',
  hydrogens: 3,
  charge: 1,
  role: 'conjugate-acid',
};
const hydroxide: BronstedSpecies = {
  formula: 'OH⁻',
  core: 'O',
  hydrogens: 1,
  charge: -1,
  role: 'conjugate-base',
};

export const CONJUGATE_CASES: ConjugateCase[] = [
  {
    id: 'hcl-water',
    title: 'Hydrochloric acid in water',
    context: 'Hydrogen chloride gas dissolves in water.',
    arrow: '→',
    species: [
      { formula: 'HCl', core: 'Cl', hydrogens: 1, charge: 0, role: 'acid' },
      water('base'),
      hydronium,
      { formula: 'Cl⁻', core: 'Cl', hydrogens: 0, charge: -1, role: 'conjugate-base' },
    ],
    note: 'HCl is a strong acid, so the forward reaction is essentially complete (→). Its conjugate base Cl⁻ is too weak to take the H⁺ back.',
  },
  {
    id: 'ammonia-water',
    title: 'Ammonia in water',
    context: 'Ammonia gas dissolves in water to make a weakly basic solution.',
    arrow: '⇌',
    species: [
      { formula: 'NH₃', core: 'N', hydrogens: 3, charge: 0, role: 'base' },
      water('acid'),
      { formula: 'NH₄⁺', core: 'N', hydrogens: 4, charge: 1, role: 'conjugate-acid' },
      hydroxide,
    ],
    note: 'Here water is the acid. With HCl it was the base. A species that can do both is amphiprotic.',
  },
  {
    id: 'ethanoic-water',
    title: 'Ethanoic acid in water',
    context: 'Vinegar contains ethanoic (acetic) acid, a weak acid.',
    arrow: '⇌',
    species: [
      { formula: 'CH₃COOH', core: 'CH₃COO', hydrogens: 1, charge: 0, role: 'acid' },
      water('base'),
      hydronium,
      { formula: 'CH₃COO⁻', core: 'CH₃COO', hydrogens: 0, charge: -1, role: 'conjugate-base' },
    ],
    note: 'Only the H on the –COOH group is donated. The three H atoms on CH₃ are not acidic.',
  },
  {
    id: 'hydrogencarbonate-base',
    title: 'Hydrogencarbonate as a base',
    context: 'Baking soda (NaHCO₃) solution is slightly basic.',
    arrow: '⇌',
    species: [
      { formula: 'HCO₃⁻', core: 'CO₃', hydrogens: 1, charge: -1, role: 'base' },
      water('acid'),
      { formula: 'H₂CO₃', core: 'CO₃', hydrogens: 2, charge: 0, role: 'conjugate-acid' },
      hydroxide,
    ],
    note: 'HCO₃⁻ accepts H⁺ here. Compare the next case, where the same ion donates H⁺: HCO₃⁻ is amphiprotic.',
  },
  {
    id: 'hydrogencarbonate-acid',
    title: 'Hydrogencarbonate as an acid',
    context: 'The same ion can also lose its hydrogen to water.',
    arrow: '⇌',
    species: [
      { formula: 'HCO₃⁻', core: 'CO₃', hydrogens: 1, charge: -1, role: 'acid' },
      water('base'),
      { formula: 'CO₃²⁻', core: 'CO₃', hydrogens: 0, charge: -2, role: 'conjugate-base' },
      hydronium,
    ],
    note: 'The products are listed in a different order this time. Match each product to the reactant it came from, not to its position.',
  },
  {
    id: 'hydrogensulfate-water',
    title: 'Hydrogensulfate ion in water',
    context: 'Sodium hydrogensulfate is used to lower the pH of pool water.',
    arrow: '⇌',
    species: [
      { formula: 'HSO₄⁻', core: 'SO₄', hydrogens: 1, charge: -1, role: 'acid' },
      water('base'),
      hydronium,
      { formula: 'SO₄²⁻', core: 'SO₄', hydrogens: 0, charge: -2, role: 'conjugate-base' },
    ],
    note: 'An anion can still be an acid. Losing H⁺ makes the charge one unit more negative: −1 becomes −2.',
  },
  {
    id: 'hcl-ammonia-gas',
    title: 'Two gases meet',
    context:
      'HCl gas and NH₃ gas meet in a tube and form a white smoke of solid ammonium chloride.',
    arrow: '→',
    species: [
      { formula: 'HCl', core: 'Cl', hydrogens: 1, charge: 0, role: 'acid' },
      { formula: 'NH₃', core: 'N', hydrogens: 3, charge: 0, role: 'base' },
      { formula: 'NH₄⁺', core: 'N', hydrogens: 4, charge: 1, role: 'conjugate-acid' },
      { formula: 'Cl⁻', core: 'Cl', hydrogens: 0, charge: -1, role: 'conjugate-base' },
    ],
    note: 'No water is involved, so the Arrhenius theory cannot describe this reaction. The Brønsted–Lowry theory can, because it only needs an H⁺ transfer.',
  },
  {
    id: 'hf-water',
    title: 'Hydrofluoric acid in water',
    context: 'HF is a weak acid even though fluorine is very electronegative.',
    arrow: '⇌',
    species: [
      { formula: 'HF', core: 'F', hydrogens: 1, charge: 0, role: 'acid' },
      water('base'),
      hydronium,
      { formula: 'F⁻', core: 'F', hydrogens: 0, charge: -1, role: 'conjugate-base' },
    ],
    note: 'Unlike HCl, HF only partly ionises, so the reaction is shown as an equilibrium (⇌).',
  },
  {
    id: 'dihydrogenphosphate-hydroxide',
    title: 'Phosphate buffer meets a base',
    context: 'Dihydrogenphosphate in a buffer neutralises added hydroxide ions.',
    arrow: '→',
    species: [
      { formula: 'H₂PO₄⁻', core: 'PO₄', hydrogens: 2, charge: -1, role: 'acid' },
      { formula: 'OH⁻', core: 'O', hydrogens: 1, charge: -1, role: 'base' },
      { formula: 'HPO₄²⁻', core: 'PO₄', hydrogens: 1, charge: -2, role: 'conjugate-base' },
      { formula: 'H₂O', core: 'O', hydrogens: 2, charge: 0, role: 'conjugate-acid' },
    ],
    note: 'Water is the conjugate acid of OH⁻. H₂PO₄⁻ still has one more H⁺ to give, so HPO₄²⁻ is itself amphiprotic.',
  },
  {
    id: 'methylamine-water',
    title: 'Methylamine in water',
    context: 'Methylamine, CH₃NH₂, has a fishy smell and is a weak base (the smell of fish comes mainly from the related amine trimethylamine).',
    arrow: '⇌',
    species: [
      { formula: 'CH₃NH₂', core: 'CH₃N', hydrogens: 2, charge: 0, role: 'base' },
      water('acid'),
      { formula: 'CH₃NH₃⁺', core: 'CH₃N', hydrogens: 3, charge: 1, role: 'conjugate-acid' },
      hydroxide,
    ],
    note: 'Like ammonia, the lone pair on nitrogen accepts the H⁺. Lemon juice reduces the smell by turning the amine into its ion.',
  },
  {
    id: 'carbonate-water',
    title: 'Carbonate ion in water',
    context: 'Washing soda (Na₂CO₃) solution is basic.',
    arrow: '⇌',
    species: [
      { formula: 'CO₃²⁻', core: 'CO₃', hydrogens: 0, charge: -2, role: 'base' },
      water('acid'),
      { formula: 'HCO₃⁻', core: 'CO₃', hydrogens: 1, charge: -1, role: 'conjugate-acid' },
      hydroxide,
    ],
    note: 'Gaining H⁺ makes the charge one unit less negative: −2 becomes −1.',
  },
  {
    id: 'ammonium-water',
    title: 'Ammonium ion in water',
    context: 'Ammonium chloride solution is slightly acidic.',
    arrow: '⇌',
    species: [
      { formula: 'NH₄⁺', core: 'N', hydrogens: 4, charge: 1, role: 'acid' },
      water('base'),
      { formula: 'NH₃', core: 'N', hydrogens: 3, charge: 0, role: 'conjugate-base' },
      hydronium,
    ],
    note: 'This is the reverse partner of ammonia’s reaction: NH₄⁺ and NH₃ are one conjugate pair, seen from the other side.',
  },
];

// ---------------------------------------------------------------------------------------------
// Weak acids and bases (25 °C)
// ---------------------------------------------------------------------------------------------

export interface WeakSpecies {
  id: string;
  name: string;
  formula: string;
  /** Conjugate partner: the conjugate base of an acid, or the conjugate acid of a base. */
  conjugate: string;
  type: 'acid' | 'base';
  /** Ka for an acid or Kb for a base at 25 °C. */
  k: number;
  source: string;
}

// prettier-ignore
export const WEAK_SPECIES: WeakSpecies[] = [
  { id: 'methanoic', name: 'Methanoic (formic) acid', formula: 'HCOOH', conjugate: 'HCOO⁻', type: 'acid', k: 1.8e-4, source: 'CRC Handbook, pKa 3.75' },
  { id: 'lactic', name: 'Lactic acid', formula: 'CH₃CH(OH)COOH', conjugate: 'CH₃CH(OH)COO⁻', type: 'acid', k: 1.4e-4, source: 'CRC Handbook, pKa 3.86' },
  { id: 'hydrofluoric', name: 'Hydrofluoric acid', formula: 'HF', conjugate: 'F⁻', type: 'acid', k: 6.3e-4, source: 'CRC Handbook, pKa 3.20' },
  { id: 'benzoic', name: 'Benzoic acid', formula: 'C₆H₅COOH', conjugate: 'C₆H₅COO⁻', type: 'acid', k: 6.3e-5, source: 'CRC Handbook, pKa 4.20' },
  { id: 'ethanoic', name: 'Ethanoic (acetic) acid', formula: 'CH₃COOH', conjugate: 'CH₃COO⁻', type: 'acid', k: 1.8e-5, source: 'CRC Handbook, pKa 4.76' },
  { id: 'hydrocyanic', name: 'Hydrocyanic acid', formula: 'HCN', conjugate: 'CN⁻', type: 'acid', k: 6.2e-10, source: 'CRC Handbook, pKa 9.21' },
  { id: 'ammonia', name: 'Ammonia', formula: 'NH₃', conjugate: 'NH₄⁺', type: 'base', k: 1.8e-5, source: 'CRC Handbook, pKb 4.75' },
  { id: 'methylamine', name: 'Methylamine', formula: 'CH₃NH₂', conjugate: 'CH₃NH₃⁺', type: 'base', k: 4.4e-4, source: 'Standard senior chemistry texts' },
];

// ---------------------------------------------------------------------------------------------
// pH and weak-species problems
// ---------------------------------------------------------------------------------------------

export type AcidBasePhSpec =
  | { kind: 'strong-acid'; formula: string; concentration: number }
  | { kind: 'strong-base'; formula: string; concentration: number; hydroxidePerFormula: 1 | 2 }
  | { kind: 'h-from-ph'; pH: number }
  | { kind: 'oh-from-h'; hydronium: number }
  | { kind: 'ph-from-oh'; hydroxide: number }
  | {
      kind: 'dilution';
      solute: 'acid' | 'base';
      formula: string;
      concentration: number;
      aliquotMl: number;
      finalMl: number;
      ionsPerFormula: 1 | 2;
    }
  | {
      kind: 'mixing';
      acid: { formula: string; concentration: number; volumeMl: number };
      base: {
        formula: string;
        concentration: number;
        volumeMl: number;
        hydroxidePerFormula: 1 | 2;
      };
    }
  | {
      kind: 'weak-acid-ph' | 'weak-acid-percent' | 'weak-base-ph';
      speciesId: string;
      concentration: number;
    }
  | { kind: 'ka-from-ph'; speciesId: string; concentration: number; pH: number }
  | { kind: 'pka-from-ka' | 'kb-from-ka'; speciesId: string };

export interface AcidBaseProblem {
  /** Stable id. Never rename: saved bindings may reference it. */
  id: string;
  mode: 'ph' | 'weak';
  title: string;
  context: string;
  spec: AcidBasePhSpec;
}

// prettier-ignore
export const PH_PROBLEMS: AcidBaseProblem[] = [
  { id: 'ph-hcl', mode: 'ph', title: 'Dilute hydrochloric acid', context: 'Find the pH of 0.010 mol L⁻¹ HCl.', spec: { kind: 'strong-acid', formula: 'HCl', concentration: 0.01 } },
  { id: 'ph-nitric', mode: 'ph', title: 'Nitric acid', context: 'Find the pH of 0.0050 mol L⁻¹ HNO₃.', spec: { kind: 'strong-acid', formula: 'HNO₃', concentration: 0.005 } },
  { id: 'ph-naoh', mode: 'ph', title: 'Sodium hydroxide', context: 'Find the pH of 0.020 mol L⁻¹ NaOH.', spec: { kind: 'strong-base', formula: 'NaOH', concentration: 0.02, hydroxidePerFormula: 1 } },
  { id: 'ph-barium-hydroxide', mode: 'ph', title: 'Barium hydroxide', context: 'Find the pH of 0.0050 mol L⁻¹ Ba(OH)₂.', spec: { kind: 'strong-base', formula: 'Ba(OH)₂', concentration: 0.005, hydroxidePerFormula: 2 } },
  { id: 'ph-hydronium-from-ph', mode: 'ph', title: 'Rainwater sample', context: 'A rainwater sample has pH 3.40. Find [H₃O⁺].', spec: { kind: 'h-from-ph', pH: 3.4 } },
  { id: 'ph-hydroxide-from-hydronium', mode: 'ph', title: 'Use Kw', context: 'A solution has [H₃O⁺] = 2.5 × 10⁻⁴ mol L⁻¹. Find [OH⁻].', spec: { kind: 'oh-from-h', hydronium: 2.5e-4 } },
  { id: 'ph-from-hydroxide', mode: 'ph', title: 'From [OH⁻] to pH', context: 'A cleaning solution has [OH⁻] = 3.2 × 10⁻³ mol L⁻¹. Find its pH.', spec: { kind: 'ph-from-oh', hydroxide: 3.2e-3 } },
  { id: 'ph-dilute-acid', mode: 'ph', title: 'Dilute an acid', context: '10.0 mL of 0.10 mol L⁻¹ HCl is made up to 250.0 mL with water. Find the new pH.', spec: { kind: 'dilution', solute: 'acid', formula: 'HCl', concentration: 0.1, aliquotMl: 10, finalMl: 250, ionsPerFormula: 1 } },
  { id: 'ph-dilute-base', mode: 'ph', title: 'Dilute a base', context: '25.0 mL of 0.20 mol L⁻¹ NaOH is made up to 500.0 mL with water. Find the new pH.', spec: { kind: 'dilution', solute: 'base', formula: 'NaOH', concentration: 0.2, aliquotMl: 25, finalMl: 500, ionsPerFormula: 1 } },
  { id: 'ph-mix-acid-excess', mode: 'ph', title: 'Mix: acid in excess', context: '25.0 mL of 0.10 mol L⁻¹ HCl is mixed with 20.0 mL of 0.10 mol L⁻¹ NaOH. Find the pH.', spec: { kind: 'mixing', acid: { formula: 'HCl', concentration: 0.1, volumeMl: 25 }, base: { formula: 'NaOH', concentration: 0.1, volumeMl: 20, hydroxidePerFormula: 1 } } },
  { id: 'ph-mix-base-excess', mode: 'ph', title: 'Mix: base in excess', context: '20.0 mL of 0.10 mol L⁻¹ HCl is mixed with 30.0 mL of 0.10 mol L⁻¹ NaOH. Find the pH.', spec: { kind: 'mixing', acid: { formula: 'HCl', concentration: 0.1, volumeMl: 20 }, base: { formula: 'NaOH', concentration: 0.1, volumeMl: 30, hydroxidePerFormula: 1 } } },
  { id: 'ph-mix-neutral', mode: 'ph', title: 'Mix: exact neutralisation', context: '25.0 mL of 0.10 mol L⁻¹ HNO₃ is mixed with 12.5 mL of 0.10 mol L⁻¹ Ba(OH)₂. Find the pH.', spec: { kind: 'mixing', acid: { formula: 'HNO₃', concentration: 0.1, volumeMl: 25 }, base: { formula: 'Ba(OH)₂', concentration: 0.1, volumeMl: 12.5, hydroxidePerFormula: 2 } } },
];

// prettier-ignore
export const WEAK_PROBLEMS: AcidBaseProblem[] = [
  { id: 'weak-ethanoic-ph', mode: 'weak', title: 'Vinegar-strength acid', context: 'Find the pH of 0.10 mol L⁻¹ ethanoic acid (Ka = 1.8 × 10⁻⁵).', spec: { kind: 'weak-acid-ph', speciesId: 'ethanoic', concentration: 0.1 } },
  { id: 'weak-ethanoic-percent', mode: 'weak', title: 'How much ionises?', context: 'Find the percentage ionisation of 0.10 mol L⁻¹ ethanoic acid (Ka = 1.8 × 10⁻⁵).', spec: { kind: 'weak-acid-percent', speciesId: 'ethanoic', concentration: 0.1 } },
  { id: 'weak-ethanoic-dilute-percent', mode: 'weak', title: 'Dilute it a hundred times', context: 'Find the percentage ionisation of 0.0010 mol L⁻¹ ethanoic acid (Ka = 1.8 × 10⁻⁵).', spec: { kind: 'weak-acid-percent', speciesId: 'ethanoic', concentration: 0.001 } },
  { id: 'weak-hf-exact', mode: 'weak', title: 'When the shortcut fails', context: 'Find the pH of 0.010 mol L⁻¹ hydrofluoric acid (Ka = 6.3 × 10⁻⁴).', spec: { kind: 'weak-acid-ph', speciesId: 'hydrofluoric', concentration: 0.01 } },
  { id: 'weak-benzoic-ph', mode: 'weak', title: 'Food preservative', context: 'Find the pH of 0.050 mol L⁻¹ benzoic acid (Ka = 6.3 × 10⁻⁵).', spec: { kind: 'weak-acid-ph', speciesId: 'benzoic', concentration: 0.05 } },
  { id: 'weak-lactic-ka', mode: 'weak', title: 'Ka from a pH reading', context: 'A 0.10 mol L⁻¹ lactic acid solution has pH 2.43. Find Ka.', spec: { kind: 'ka-from-ph', speciesId: 'lactic', concentration: 0.1, pH: 2.43 } },
  { id: 'weak-methanoic-ka', mode: 'weak', title: 'Ka of methanoic acid', context: 'A 0.20 mol L⁻¹ methanoic acid solution has pH 2.23. Find Ka.', spec: { kind: 'ka-from-ph', speciesId: 'methanoic', concentration: 0.2, pH: 2.23 } },
  { id: 'weak-ammonia-ph', mode: 'weak', title: 'Household ammonia', context: 'Find the pH of 0.10 mol L⁻¹ ammonia (Kb = 1.8 × 10⁻⁵).', spec: { kind: 'weak-base-ph', speciesId: 'ammonia', concentration: 0.1 } },
  { id: 'weak-methylamine-ph', mode: 'weak', title: 'A stronger weak base', context: 'Find the pH of 0.020 mol L⁻¹ methylamine (Kb = 4.4 × 10⁻⁴).', spec: { kind: 'weak-base-ph', speciesId: 'methylamine', concentration: 0.02 } },
  { id: 'weak-hcn-pka', mode: 'weak', title: 'pKa of hydrocyanic acid', context: 'HCN has Ka = 6.2 × 10⁻¹⁰. Find pKa.', spec: { kind: 'pka-from-ka', speciesId: 'hydrocyanic' } },
  { id: 'weak-ethanoate-kb', mode: 'weak', title: 'Kb of the conjugate base', context: 'Ethanoic acid has Ka = 1.8 × 10⁻⁵. Find Kb of the ethanoate ion, CH₃COO⁻.', spec: { kind: 'kb-from-ka', speciesId: 'ethanoic' } },
];
